import React, { useRef, useContext, useState, useMemo } from "react";
import { makeStyles } from "@mui/styles";
import {
  Box,
  Typography,
  Unstable_Grid2 as Grid,
  Checkbox,
  Button,
  TextField,
  ButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel
} from "@mui/material";
import { AlertTemplateDialog } from "containers";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { default as _omit } from "lodash/omit";
import { default as _pick } from "lodash/pick";
import { ISXContext, TagRoleClipboardContext } from "services/Utils";
import TagRoleProperties, { TagGeneralAttributes } from "./TagRoleProperties";
import TagValueCalculation from "./TagValueCalculation";

import { getRole, QUANTITY_ROLE, STATES_ROLE, COUNTS_ROLE } from "services/RolesService";

const useStyles = makeStyles({
  copyPasteButton: {
    fontSize: "0.625rem",
    textTransform: "none",
    padding: 0,
  },
});

const RolePropertiesAccordion = ({
  role: r,
  tagId,
  tagProperties,
  defaultExpanded,
  useClipboard,
  handleTagPropsChange,
  readOnly,
  children,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded || false);

  const clipboardContext = useContext(TagRoleClipboardContext);

  const classes = useStyles();

  const editableAttributes = useMemo(
    () =>
      Object.entries(r.attributes)
        .filter(([_, v]) => v.editable !== false)
        .map(([k, _]) => k),
    [r.attributes]
  );

  return (
    <Accordion
      square
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      style={{
        boxShadow: "unset",
        width: "100%",
        paddingTop: 24,
        paddingLeft: 32,
        paddingRight: 32,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${r.guuid}-role-panel-content`}
        id={`${r.guuid}-role-panel-header`}
        style={{
          flexDirection: "row-reverse",
          paddingLeft: 0,
        }}
      >
        <Typography variant="overline">{r.name}</Typography>
        {useClipboard &&
          !readOnly &&
          expanded &&
          editableAttributes.length > 0 && (
            <ButtonGroup variant="text" color="primary">
              <Button
                className={classes.copyPasteButton}
                onClick={(event) => {
                  const contents = _pick(tagProperties, editableAttributes);
                  clipboardContext.setClipboard({
                    tagId,
                    roleId: r.guuid,
                    contents,
                  });
                  event.stopPropagation();
                }}
              >
                copy
              </Button>
              {clipboardContext.clipboard &&
                clipboardContext.clipboard.roleId === r.guuid &&
                clipboardContext.clipboard.tagId !== tagId && (
                  <Button
                    className={classes.copyPasteButton}
                    onClick={(event) => {
                      handleTagPropsChange(
                        tagId,
                        clipboardContext.clipboard.contents
                      );
                      event.stopPropagation();
                    }}
                  >
                    paste
                  </Button>
                )}
            </ButtonGroup>
          )}
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
};

const ValueCalculationAccordion = ({
  defaultExpanded,
  children,
  advanced,
  setAdvanced
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded || false);
  return (
    <Accordion
      square
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      style={{
        boxShadow: "unset",
        width: "100%",
        paddingTop: 24,
        paddingLeft: 32,
        paddingRight: 32,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        style={{
          flexDirection: "row-reverse",
          paddingLeft: 0,
        }}
      >
        <Typography variant="overline">Calculated Value&nbsp;&nbsp;&nbsp;</Typography>
        <div> <FormControlLabel control={<Checkbox
                                           size="small" checked={advanced}
                                           onChange={ (e) =>  { e.stopPropagation(); setAdvanced(!advanced) }}
                                           onClick={(e) => { e.stopPropagation() } }
                                         />} label="Expert Mode"
                                onClick={(event) => event.stopPropagation()} // redundant but safe                                
              /> </div>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
};



// need full set of "protected props"
const PROTECTED_TAG_PROPS = ["alarms", "s_int", "roles"];

const TagConfig = ({
  stack,
  tagId,
  tagProperties = {},
  handleTagPropChange,
  handleTagPropsChange,
  create = false,
  showStackLabel = false,
  showCheck = true,
  checked = false,
  onCheckChange,
  useClipboard,
  readOnly = true,
  predefined,
}) => {
  const SHOWOTHERPROPS = false;

  const [openAlertTemplateDialog, setOpenAlertTemplateDialog] = useState(false);
  const [newTagId, setNewTagId] = useState("");
  const [advanced, setAdvanced] = useState(false);

  const stackLabel = useRef(
    stack?.name || JSON.parse(stack.dev_session)["stack.label"] || "<Unnamed>"
  );

  const isxContext = useContext(ISXContext);

  let leftoverTagProperties = SHOWOTHERPROPS
    ? _omit(tagProperties ?? {}, PROTECTED_TAG_PROPS)
    : {};
  let roleIds = tagProperties.roles ?? [];
  if (roleIds.indexOf(QUANTITY_ROLE.guuid) === -1) {
    roleIds = [QUANTITY_ROLE.guuid, ...roleIds];
  }

  if (roleIds.indexOf(STATES_ROLE.guuid) === -1) {
    roleIds = [...roleIds, STATES_ROLE.guuid];
  }
  if (roleIds.indexOf(COUNTS_ROLE.guuid) === -1) {
    roleIds = [...roleIds, COUNTS_ROLE.guuid];
  }

  const roles = roleIds
    .map((rid) => getRole(rid))
    .filter((r) => r && r.exposed !== false);
  roles.forEach((r) => {
    leftoverTagProperties = _omit(
      leftoverTagProperties,
      Object.keys(r.attributes ?? {})
    );
  });

  const handleAlertTemplateDialogOpen = (event) => {
    //setAnchorEl(null);
    setOpenAlertTemplateDialog(true);
  };

  const handleAlertTemplateDialogClose = (event) => {
    setOpenAlertTemplateDialog(false);
  };

  const handleNewTagId = (v) => {
    if(create) create(v);
    setNewTagId(v);
  };
  
  const leftoverTagPropsMetadata = Object.entries(leftoverTagProperties).map(
    ([p, v]) => {
      const metadata = { exposed: true };
      const vtype = typeof v;
      if (vtype === "string" || vtype === "number") {
        metadata.type = vtype;
      }
      return [p, metadata];
    }
  );

  return (
    <div id="TagConfig">
      <Grid container sx={{ paddingTop: 2, paddingBottom: 2 }}>
        <Grid
          container
          xs={12}
          spacing={1}
          wrap="wrap"
          justifyContent="space-around"
          // className={classes.innerContainer}
          sx={{
            borderRadius: 2,
            borderColor: "lightgray",
            borderWidth: 3,
            borderStyle: "solid",
          }}
        >
          <Grid xs={12}>
            <div
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {showCheck && (
                  <Checkbox checked={checked} onChange={onCheckChange} />
                )}
                {create ?
                 <TextField
                  variant="standard"
                  label="Tag ID"
                  fullWidth
                  type="string"
                  value={newTagId}
                  InputLabelProps={{ sx: { fontSize: 12 } }}
                  InputProps={{ sx: { fontSize: 12 } }}
                  onChange={(event) => handleNewTagId(event.target.value)
                  }
                />
                 :
                 <>
                   <Typography>Tag ID:&nbsp;</Typography>
                   <Typography sx={{ fontWeight: 500 }}>
                     {showStackLabel ? `${stackLabel.current}:${tagId}` : tagId}
                   </Typography>
                 </>}
              </Box>
              {isxContext.alertTemplates && (
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  onClick={handleAlertTemplateDialogOpen}
                  disabled={readOnly}
                >
                  Auto Set Alerts
                </Button>
              )}
            </div>
          </Grid>
          <Grid container xs={12} sx={{ overflow: "auto" }}>
            {roles.map((r, idx) => {
              return (
                <Grid
                  key={r.guuid}
                  container
                  xs={12}
                  sx={{ paddingBottom: 1 }}
                >
                  {idx === 0 ? (
                    <Box
                      sx={{
                        width: "100%",
                        paddingLeft: 4,
                        paddingRight: 4,
                      }}
                    >
                      <Grid xs={12}>
                        <Typography variant="overline">{r.name}</Typography>
                      </Grid>
                      <TagRoleProperties
                        {...{
                          tagId,
                          tagProperties,
                          role: r,
                          handleTagPropChange,
                          handleTagPropsChange,
                          readOnly,
                          accordion: idx !== 0,
                          expanded: idx === 0,
                        }}
                      />
                    </Box>
                  ) : (
                    <RolePropertiesAccordion
                      {...{
                        role: r,
                        tagId: tagId,
                        tagProperties,
                        handleTagPropsChange,
                        useClipboard,
                        defaultExpanded: idx === 0,
                        readOnly,
                      }}
                    >
                      <TagRoleProperties
                        {...{
                          tagId,
                          tagProperties,
                          role: r,
                          handleTagPropChange,
                          handleTagPropsChange,
                          accordion: idx !== 0,
                          expanded: idx === 0,
                          readOnly,
                        }}
                      />
                    </RolePropertiesAccordion>
                  )}
                </Grid>
              );
            })}
            {leftoverTagPropsMetadata.length > 0 && (
              <Grid container xs={12}>
                <Grid xs={12}>
                  <Typography variant="overline">Other Properties</Typography>
                </Grid>
                <TagGeneralAttributes
                  attributes={leftoverTagPropsMetadata}
                  tagId={tagId}
                  tagProperties={tagProperties}
                  handleTagPropChange={handleTagPropChange}
                  readOnly={readOnly}
                />
              </Grid>
            )}
          </Grid>
        <Grid container xs={12} sx={{ overflow: "auto" }}>
          { (true || tagProperties?.isx_tag_user_writable) && //todo remove the true?
              <ValueCalculationAccordion advanced={advanced} setAdvanced={setAdvanced} >
                <TagValueCalculation readOnly={readOnly}
                                     derived={tagProperties["$derived"]}
                                     advanced={advanced}
                                     predefined={predefined}
                                     setDerived={ (v) => handleTagPropChange(tagId, "$derived", v) } />
              </ValueCalculationAccordion>
            }
          </Grid>
        </Grid>
      </Grid>
      <AlertTemplateDialog
        tag={tagId}
        stack={stack}
        open={openAlertTemplateDialog}
        templates={isxContext.alertTemplates}
        handleClose={handleAlertTemplateDialogClose}
      />
    </div>
  );
};

export default TagConfig;
